import React, { useRef, useState } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import Button from './Button';
import './FileUpload.css';

interface FileUploadProps {
    onFilesSelected: (files: File[]) => void;
    accept?: string;
    multiple?: boolean;
    maxSize?: number; // in MB
    maxFiles?: number;
}

const FileUpload: React.FC<FileUploadProps> = ({
    onFilesSelected,
    accept = '.pdf',
    multiple = true,
    maxSize = 50,
    maxFiles = 10,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [error, setError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const getFileConfig = () => {
        if (accept?.includes('image')) {
            return {
                label: '拖放图片文件到这里',
                hint: '支持 JPG, PNG, GIF, BMP, WebP 等常见格式',
                validate: (file: File) => file.type.startsWith('image/'),
                error: '只支持图片文件'
            };
        } else if (accept?.includes('.docx') || accept?.includes('.xlsx')) {
            const getHint = () => {
                const types = [];
                if (accept.includes('.pdf')) types.push('PDF');
                if (accept.includes('.docx')) types.push('Word');
                if (accept.includes('.xlsx')) types.push('Excel');
                return types.length > 0 ? `支持 ${types.join(', ')} 格式` : '支持文档格式';
            };
            return {
                label: '拖放文档到这里',
                hint: getHint(),
                validate: (file: File) => {
                    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                    return accept.includes(ext);
                },
                error: '只支持指定格式的文档'
            };
        }
        return {
            label: '拖放 PDF 文件到这里',
            hint: '支持 PDF 格式',
            validate: (file: File) => file.name.toLowerCase().endsWith('.pdf'),
            error: '只支持 PDF 文件'
        };
    };

    const config = getFileConfig();

    const validateFiles = (files: File[]): { valid: File[]; error: string } => {
        const validFiles: File[] = [];
        let errorMsg = '';

        // Check file count
        if (!multiple && files.length > 1) {
            errorMsg = '只能上传一个文件';
            return { valid: [], error: errorMsg };
        }

        if (selectedFiles.length + files.length > maxFiles) {
            errorMsg = `最多只能上传 ${maxFiles} 个文件`;
            return { valid: [], error: errorMsg };
        }

        // Validate each file
        for (const file of files) {
            // Check file type
            if (!config.validate(file)) {
                errorMsg = config.error;
                continue;
            }

            // Check file size
            const fileSizeMB = file.size / (1024 * 1024);
            if (fileSizeMB > maxSize) {
                errorMsg = `文件大小不能超过 ${maxSize}MB`;
                continue;
            }

            validFiles.push(file);
        }

        return { valid: validFiles, error: errorMsg };
    };

    const handleFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);
        const { valid, error: validationError } = validateFiles(fileArray);

        if (validationError) {
            setError(validationError);
            setTimeout(() => setError(''), 3000);
        }

        if (valid.length > 0) {
            const newFiles = [...selectedFiles, ...valid];
            setSelectedFiles(newFiles);
            onFilesSelected(newFiles);
            setError('');
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        handleFiles(files);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const removeFile = (index: number) => {
        const newFiles = selectedFiles.filter((_, i) => i !== index);
        setSelectedFiles(newFiles);
        onFilesSelected(newFiles);
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="file-upload">
            <div
                className={`upload-zone ${isDragging ? 'dragging' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <Upload className="upload-icon" size={48} />
                <h3>{config.label}</h3>
                <p>或者</p>
                <Button variant="outline" onClick={handleBrowseClick}>
                    浏览文件
                </Button>
                <p className="upload-hint">
                    {config.hint}，单个文件最大 {maxSize}MB
                    {multiple && `，最多 ${maxFiles} 个文件`}
                </p>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />

            {error && (
                <div className="upload-error">
                    {error}
                </div>
            )}

            {selectedFiles.length > 0 && (
                <div className="file-list">
                    <h4>已选择的文件 ({selectedFiles.length})</h4>
                    {selectedFiles.map((file, index) => (
                        <div key={index} className="file-item">
                            <FileText size={24} className="file-icon" />
                            <div className="file-info">
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">{formatFileSize(file.size)}</span>
                            </div>
                            <button
                                className="file-remove"
                                onClick={() => removeFile(index)}
                                aria-label="删除文件"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUpload;
